import { Request, Response, NextFunction, RequestHandler } from 'express';
import multer from 'multer';
import path from 'path';
import { HttpError } from './error-handler';
import { getConfig } from './config';
import { UploadOptions } from './types';

/**
 * Configure storage for multer
 * @param options Upload options
 * @returns Multer storage engine
 */
function configureStorage(options: UploadOptions = {}): multer.StorageEngine {
  if (options.destination) {
    return multer.diskStorage({
      destination: options.destination,
      filename: (req, file, cb) => {
        // Generate a unique filename
        const uniqueSuffix = Date.now() + '-' + Math.round(Math.random() * 1E9);
        const originalExt = path.extname(file.originalname);
        const filename = path.basename(file.originalname, originalExt);
        cb(null, `${filename}-${uniqueSuffix}${originalExt}`);
      }
    });
  }

  // Default to memory storage
  return multer.memoryStorage();
}

/**
 * Create file upload middleware
 * @param fieldName Name of the field for the file
 * @param options Upload options
 */
export function upload(
  fieldName: string = 'file',
  options: {
    maxCount?: number;
    maxSize?: number;
    allowedMimeTypes?: string[];
    required?: boolean;
    destination?: string;
  } = {}
): RequestHandler {
  // Get middleware configuration
  const config = getConfig();

  // Create multer instance with file size limits
  const uploader = multer({
    storage: configureStorage({
      ...config.uploads,
      destination: options.destination || config.uploads?.destination
    }),
    limits: {
      fileSize: options.maxSize || config.uploads?.maxSize,
    },
    fileFilter: (req, file, cb) => {
      const allowedTypes = options.allowedMimeTypes || config.uploads?.allowedMimeTypes;

      if (!allowedTypes || allowedTypes.includes(file.mimetype)) {
        cb(null, true);
      } else {
        cb(null, false);
        return cb(new Error(`Invalid file type. Allowed types: ${allowedTypes?.join(', ')}`));
      }
    }
  });

  // Use single or array upload based on maxCount
  const uploadMiddleware = options.maxCount && options.maxCount > 1
    ? uploader.array(fieldName, options.maxCount)
    : uploader.single(fieldName);

  return (req: Request, res: Response, next: NextFunction) => {
    uploadMiddleware(req, res, (err) => {
      if (err) {
        if (err instanceof multer.MulterError) {
          // Multer error (e.g., file too large)
          return next(new HttpError(
            err.message,
            400,
            'UPLOAD_ERROR'
          ));
        } else {
          // Other errors (e.g., file type validation)
          return next(new HttpError(
            err.message,
            400,
            'UPLOAD_ERROR'
          ));
        }
      }

      // Check if file is required
      if (options.required) {
        if ((options.maxCount && options.maxCount > 1 && (!req.files || (req.files as Express.Multer.File[]).length === 0)) ||
          (!options.maxCount && !req.file)) {
          return next(new HttpError(
            `No ${fieldName} provided`,
            400,
            'UPLOAD_ERROR'
          ));
        }
      }

      next();
    });
  };
}

/**
 * Helper function to access uploaded file data
 * @param req Request object
 * @param fieldName Field name used in upload middleware
 */
export function getUploadedFile(req: Request, fieldName: string = 'file'): Express.Multer.File | null {
  return req.file || null;
}

/**
 * Helper function to access uploaded files data
 * @param req Request object
 * @param fieldName Field name used in upload middleware
 */
export function getUploadedFiles(req: Request, fieldName: string = 'files'): Express.Multer.File[] | null {
  return req.files as Express.Multer.File[] || null;
}
